import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookmark, FiPlay, FiTrash2, FiPlus, FiClock } = FiIcons;

const LessonBookmarks = ({ lessonId, bookmarks, onAdd, onDelete, onSeek }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBookmark, setNewBookmark] = useState({
    timestamp: '',
    note: ''
  });

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const parseTimeInput = (timeStr) => {
    // Parse time input like "1:30" or "90" (seconds)
    if (timeStr.includes(':')) {
      const [mins, secs] = timeStr.split(':');
      return parseInt(mins) * 60 + parseInt(secs || 0);
    }
    return parseInt(timeStr) || 0;
  };

  const handleAddBookmark = (e) => {
    e.preventDefault();
    const timestamp = parseTimeInput(newBookmark.timestamp);
    
    if (timestamp >= 0) {
      onAdd(timestamp, newBookmark.note);
      setNewBookmark({ timestamp: '', note: '' });
      setShowAddForm(false);
    }
  };

  const sortedBookmarks = [...bookmarks].sort((a, b) => a.timestamp - b.timestamp);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiBookmark} className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Bookmarks</h3>
              <span className="text-sm text-gray-500">({bookmarks.length})</span>
            </div>
            
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center space-x-1 px-3 py-2 bg-nutrition-600 text-white rounded-md hover:bg-nutrition-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add Bookmark</span>
            </button>
          </div>
        </div>

        {/* Add Bookmark Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-b border-gray-200 bg-gray-50 overflow-hidden"
            >
              <form onSubmit={handleAddBookmark} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timestamp
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={newBookmark.timestamp}
                        onChange={(e) => setNewBookmark({ ...newBookmark, timestamp: e.target.value })}
                        placeholder="1:30 or 90"
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Format: 1:30 or 90 (seconds)</p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Note (optional)
                    </label>
                    <input
                      type="text"
                      value={newBookmark.note}
                      onChange={(e) => setNewBookmark({ ...newBookmark, note: e.target.value })}
                      placeholder="Add a note for this bookmark..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-nutrition-600 text-white text-sm rounded-md hover:bg-nutrition-700 transition-colors"
                  >
                    Add Bookmark
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bookmarks List */}
        <div className="p-6">
          {sortedBookmarks.length === 0 ? (
            <div className="text-center py-12">
              <SafeIcon icon={FiBookmark} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No bookmarks yet</h4>
              <p className="text-gray-500 mb-4">
                Add bookmarks to mark important moments in the video
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Add your first bookmark</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedBookmarks.map((bookmark) => (
                <motion.div
                  key={bookmark.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <button
                      onClick={() => onSeek(bookmark.timestamp)}
                      className="flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700"
                    >
                      <SafeIcon icon={FiPlay} className="w-4 h-4" />
                      <span className="font-mono text-sm font-medium">
                        {formatTime(bookmark.timestamp)}
                      </span>
                    </button>
                    
                    {bookmark.note && (
                      <div className="flex-1">
                        <p className="text-gray-700 text-sm">{bookmark.note}</p>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => onDelete(bookmark.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Tips */}
        {sortedBookmarks.length > 0 && (
          <div className="px-6 pb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Bookmark Tips:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Click the play button to jump to that timestamp</li>
                <li>• Bookmarks are sorted by time automatically</li>
                <li>• Add notes to remember why you bookmarked that moment</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default LessonBookmarks;